import { z } from 'zod';

/**
 * The `groupId` is a unique ID for semantically related suggested edits.
 * This value is shared with the suggestion markers in the document which
 * indicate the edges of the suggested edit group.
 */
export const SuggestionSourceSchema = z.object({
  groupId: z.string(),
  messageId: z.string(),
  threadId: z.string(),
  type: z.literal('ai-chat'),
});

export type SuggestionSource = z.infer<typeof SuggestionSourceSchema>;
export type AiChatSuggestionSource = SuggestionSource;
export type SuggestionSourceWithAuthorId = SuggestionSource & { authorId: string };

/**
 * Builds the source stamp for one suggestion group. Chat closes over its
 * messageId/threadId. Returning `undefined` materializes the group as the
 * author's own edits — no source on any edit and no suggestion markers —
 * indistinguishable from tracked changes the user made by hand. Playbook
 * review accept applies this way: the reviewer decided in the panel, so
 * nothing AI-flavored should land in the document.
 */
export type MakeSuggestionSource = (groupId: string) => SuggestionSource | undefined;

/**
 * Builds an `ai-chat` SuggestionSource. Centralizes the `type: 'ai-chat'`
 * literal so all call sites stay in sync with the schema.
 */
export function makeAiChatSource({
  groupId,
  messageId,
  threadId,
}: {
  groupId: string;
  messageId: string;
  threadId: string;
}): AiChatSuggestionSource {
  return { groupId, messageId, threadId, type: 'ai-chat' };
}

export function getIsAiChatSource(source: SuggestionSource): source is AiChatSuggestionSource {
  return source.type === 'ai-chat';
}

/**
 * The id of the generation event that produced a source — the chat
 * message. Used where per-generation identity matters (e.g. layering diffs
 * from successive generations).
 */
export function getSuggestionSourceOriginId(source: SuggestionSource): string {
  return source.messageId;
}

/** Structural equality between two source stamps. */
export function getIsSameSuggestionSource(a: SuggestionSource, b: SuggestionSource): boolean {
  return a.groupId === b.groupId && a.messageId === b.messageId && a.threadId === b.threadId;
}

/**
 * Determines whether an existing insertion can be cancelled (rejected/removed)
 * by a new deletion, rather than having a deleteNode layered on top.
 *
 * Cancellation is appropriate when both edits come from the same "context":
 * - Both have no source (user manually cancelling their own insert)
 * - Both have a source with the same type (e.g., both 'ai-chat')
 *
 * Layering is appropriate when one has a source and the other doesn't (AI
 * deleting a user's insert).
 *
 * This check is used alongside an authorId check — the caller verifies
 * authorId equality before calling this function.
 */
export function getIsCancellableEdit(
  existingSource: SuggestionSource | undefined,
  deletionSource: SuggestionSource | undefined
): boolean {
  if (!existingSource && !deletionSource) return true;
  if (!existingSource || !deletionSource) return false;
  return existingSource.type === deletionSource.type;
}
